// ---------------------------------------------------------------------------
// PWA Caisse Tablette — useTables hook
// Provides access to floor plans and table state from Zustand,
// and exposes table actions (lock, release, instant reservation).
// ---------------------------------------------------------------------------

import { useEffect, useCallback } from 'react';
import { useTableStore } from '@/store/table-store';
import { tableSync } from '@/services/table-sync';
import type { FloorPlan, Table } from '@/lib/types';

// ---------------------------------------------------------------------------
// Return type
// ---------------------------------------------------------------------------

interface UseTablesReturn {
  /** All floor plans. */
  floorPlans: FloorPlan[];
  /** Table statuses keyed by table ID. */
  tables: Record<string, Table>;
  /** The currently selected table ID (or null). */
  selectedTableId: string | null;
  /** Select a table by ID. */
  selectTable: (tableId: string | null) => void;
  /** Lock a table for a given duration. */
  lockTable: (tableId: string, duration?: number) => Promise<void>;
  /** Release (unlock) a table. */
  releaseTable: (tableId: string) => Promise<void>;
  /** Create an instant (walk-in) reservation. */
  createInstantReservation: (
    tableId: string,
    clientInfo: { name?: string; phone?: string; nb_persons?: number },
  ) => Promise<void>;
  /** Manually refresh tables from the backend. */
  refresh: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Hook that loads floor plans and table statuses on mount and
 * provides table management actions.
 */
export function useTables(): UseTablesReturn {
  const floorPlans = useTableStore((s) => s.floorPlans);
  const tables = useTableStore((s) => s.tables);
  const selectedTableId = useTableStore((s) => s.selectedTableId);
  const selectTable = useTableStore((s) => s.selectTable);

  useEffect(() => {
    // Load initial data on mount
    tableSync.loadFloorPlans().catch(() => {});
    tableSync.syncTables().catch(() => {});
  }, []);

  const lockTable = useCallback(
    async (tableId: string, duration?: number): Promise<void> => {
      await tableSync.lockTable(tableId, duration);
    },
    [],
  );

  const releaseTable = useCallback(
    async (tableId: string): Promise<void> => {
      await tableSync.releaseTable(tableId);
    },
    [],
  );

  const createInstantReservation = useCallback(
    async (
      tableId: string,
      clientInfo: { name?: string; phone?: string; nb_persons?: number },
    ): Promise<void> => {
      await tableSync.createInstantReservation(tableId, clientInfo);
    },
    [],
  );

  const refresh = useCallback(async (): Promise<void> => {
    await tableSync.loadFloorPlans();
    await tableSync.syncTables();
  }, []);

  return {
    floorPlans,
    tables,
    selectedTableId,
    selectTable,
    lockTable,
    releaseTable,
    createInstantReservation,
    refresh,
  };
}
